/**
 * PR-V0c — live in-canvas text-input overlay (TEXT / MTEXT drafting +
 * in-place edit of existing text).
 *
 * Mounts as a sibling DOM node of the R3F `<Canvas>` (NOT inside it —
 * R3F's reconciler must never see DOM elements; PR-K.1 root cause).
 *
 * # AutoCAD parity
 *
 *   - Single-line `<input>` for TEXT entities; multi-line `<textarea>`
 *     for MText entities.
 *   - Position projected from world coords via the shared
 *     `cameraSnapshot.ts` bridge — overlay reflows on pan, zoom, and
 *     resize automatically.
 *   - Font height scales with `cameraZoom` so the visible text on the
 *     overlay matches the scale of the surrounding entities at every
 *     zoom level (matches AutoCAD's MText editor — entity height is
 *     world-space, but the editor renders glyphs at that height post-
 *     zoom).
 *   - Enter (no Shift) commits via `onSubmit`; Shift+Enter inserts a
 *     newline in multiline mode (textarea's default behavior).
 *   - Escape cancels via `onCancel`.
 *   - Tab commits (Tab field-advance is deferred — call onSubmit and
 *     let the host advance to the next field if applicable).
 *   - onBlur commits (AutoCAD MText editor commits on focus loss to
 *     a non-MText element).
 *
 * # IME composition support
 *
 * Asian input methods (IME) emit `compositionstart` / `compositionend`
 * events around a multi-key composition (e.g. typing Japanese, Chinese,
 * Korean characters). `onChange` events fire mid-composition with
 * partial / candidate text — committing those would corrupt the
 * intended character. We track a `composing` flag and skip the
 * `onTextChange` callback while it is true; the final
 * `compositionend` resets the flag and forwards the resolved text.
 */

import * as React from "react";
import { useEffect, useRef, useState } from "react";

import { projectWorldToClient } from "../cameraSnapshot";
import { applyOffsetToPoint2d } from "../coordinateOrigin";
import { useCameraSnapshotVersion } from "../grips/gripsOverlay";

/**
 * Shape of the props consumed by `<LiveTextOverlay>`. Mirrors the
 * `LiveTextInputProps` contract from `Scene3DStageProps.ts` plus an
 * optional `entityId` for the in-place-edit variant (so testid is
 * uniquely scoped per active overlay).
 */
export interface LiveTextOverlayProps {
  /** Current text being edited. Controlled — caller owns state. */
  readonly text: string;
  /**
   * Insertion point in DOCUMENT (AutoCAD/DXF) coordinates.
   *
   * DRAFT-DOCUMENT-FRAME — both call sites supply document values: the
   * drafting variant reads `draftSession.points` (the draft state machine
   * works in document coordinates) and the in-place-edit variant reads the
   * entity's own `Insertion X/Y` / `Landing X/Y` property bag. This
   * component owns the single conversion into the OFFSET-world frame
   * `projectWorldToClient` projects through, so neither caller can get it
   * wrong and the two can never disagree.
   */
  readonly position: { x: number; y: number };
  /** Text height in world units. */
  readonly height: number;
  /** Rotation in degrees (CCW). */
  readonly rotation: number;
  /** Multiline mode — renders a `<textarea>` instead of `<input>`. */
  readonly multiline: boolean;
  /** Optional MText bbox width (world units). */
  readonly width: number | undefined;
  readonly onTextChange: (text: string) => void;
  readonly onSubmit: () => void;
  readonly onCancel: () => void;
  /** When set, used as a scoping suffix on the testid. */
  readonly entityId?: string;
  /** When false the overlay does not render at all. */
  readonly hidden?: boolean;
}

/**
 * Live text overlay component. Renders an absolutely-positioned input
 * or textarea projected to the cursor's world position.
 *
 * Returns `null` when no camera snapshot is available yet (the very
 * first render before R3F's first `useFrame` has fired) — the host
 * should mount us unconditionally; we self-hide until projection works.
 */
export function LiveTextOverlay(
  props: LiveTextOverlayProps,
): React.ReactElement | null {
  const {
    text,
    position,
    height,
    rotation,
    multiline,
    width,
    onTextChange,
    onSubmit,
    onCancel,
    entityId,
    hidden,
  } = props;

  // Subscribe to the camera snapshot version so we re-project on
  // pan/zoom/resize. Returning the version itself unused — we only
  // care about the re-render side effect.
  useCameraSnapshotVersion();

  // IME composition guard. While a composition is in progress
  // (Asian input methods, dead-key sequences) we suppress
  // `onTextChange` callbacks because the value is partial / candidate
  // text. The final compositionend fires `onTextChange` with the
  // resolved text.
  const composingRef = useRef<boolean>(false);
  const [composingTick, setComposingTick] = useState(0);

  // Auto-focus on mount + on content prop change (so re-arming the
  // overlay for a new entity refocuses the input).
  const inputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (hidden) return;
    if (multiline) {
      textareaRef.current?.focus();
    } else {
      inputRef.current?.focus();
    }
  }, [multiline, hidden, entityId]);

  if (hidden) return null;

  // Project insertion point to client px. Uses the shared snapshot;
  // returns null until the first frame has rendered.
  //
  // DRAFT-DOCUMENT-FRAME — `position` is a DOCUMENT coordinate (see the prop
  // doc) while the camera behind `projectWorldToClient` lives in the
  // renderer's OFFSET-world frame, so the origin comes off here, once, for
  // every projection below (insertion, height anchor, width anchor). The
  // `height` / `width` deltas are translation-invariant and stay raw.
  const positionRender = applyOffsetToPoint2d(position);
  const client = projectWorldToClient(positionRender.x, positionRender.y);
  if (!client) return null;

  // World-height → CSS-pixel font size. Project a second world point
  // (insertion + height) and measure the projected delta. Cheap (one
  // unproject) and avoids hard-coding a zoom-snapshot accessor here.
  const heightAnchor = projectWorldToClient(
    positionRender.x,
    positionRender.y + height,
  );
  // CAD Y is up, screen Y is down — projected delta is negative for a
  // height that points "up" in world. Take abs so font-size is positive.
  const fontSizePx = heightAnchor
    ? Math.max(8, Math.abs(client.y - heightAnchor.y))
    : Math.max(8, height);
  // Width handling: MTEXT bounding box is provided in world units; we
  // project a second world point to derive the CSS pixel width.
  const widthAnchor =
    width !== undefined
      ? projectWorldToClient(positionRender.x + width, positionRender.y)
      : null;
  const widthPx =
    widthAnchor !== null && widthAnchor !== undefined
      ? Math.max(40, Math.abs(widthAnchor.x - client.x))
      : multiline
        ? 240
        : 200;

  // Common keyboard handler.
  const onKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    // Suppress Enter / Escape behavior while composing — IME confirms
    // composition via Enter, that should not commit the overlay.
    if (composingRef.current) return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === "Enter") {
      // Multiline + Shift+Enter → newline (textarea default).
      if (multiline && e.shiftKey) return;
      e.preventDefault();
      e.stopPropagation();
      onSubmit();
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      onSubmit();
      return;
    }
  };

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    // Skip mid-composition events — the partial value is candidate
    // text. The compositionend handler below forwards the resolved
    // value.
    if (composingRef.current) return;
    onTextChange(e.target.value);
  };

  const onCompositionStart = (): void => {
    composingRef.current = true;
    setComposingTick((t) => t + 1);
  };

  const onCompositionEnd = (
    e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    composingRef.current = false;
    setComposingTick((t) => t + 1);
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    onTextChange(target.value);
  };

  const onBlur = (): void => {
    // AutoCAD parity: focus loss commits the edit. Skip while
    // composing so a candidate-window blur doesn't corrupt commits.
    if (composingRef.current) return;
    onSubmit();
  };

  const baseStyle: React.CSSProperties = {
    position: "absolute",
    left: 0,
    top: 0,
    transform: `translate(${client.x}px, ${client.y - fontSizePx}px) rotate(${-rotation}deg)`,
    transformOrigin: `0 ${fontSizePx}px`,
    fontFamily: "Roboto, 'Roboto Regular', monospace",
    fontSize: fontSizePx,
    lineHeight: `${fontSizePx * 1.2}px`,
    padding: "0 2px",
    color: "#ffffff",
    background: "rgba(0, 0, 0, 0.65)",
    border: "1px dashed rgba(255, 213, 138, 0.85)",
    outline: "none",
    pointerEvents: "auto",
    zIndex: 6,
    boxSizing: "border-box",
  };

  const testIdSuffix = entityId !== undefined ? `-${entityId}` : "";

  if (multiline) {
    const minHeight = fontSizePx * 1.4 * 3;
    return (
      <textarea
        ref={textareaRef}
        data-testid={`scene3d-live-text${testIdSuffix}`}
        data-composing={composingTick > 0 && composingRef.current ? "1" : "0"}
        value={text}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onCompositionStart={onCompositionStart}
        onCompositionEnd={onCompositionEnd}
        onBlur={onBlur}
        style={{
          ...baseStyle,
          width: widthPx,
          minHeight,
          resize: "none",
        }}
      />
    );
  }
  return (
    <input
      ref={inputRef}
      type="text"
      data-testid={`scene3d-live-text${testIdSuffix}`}
      data-composing={composingTick > 0 && composingRef.current ? "1" : "0"}
      value={text}
      onChange={onChange}
      onKeyDown={onKeyDown}
      onCompositionStart={onCompositionStart}
      onCompositionEnd={onCompositionEnd}
      onBlur={onBlur}
      style={{
        ...baseStyle,
        width: widthPx,
        height: fontSizePx * 1.4,
      }}
    />
  );
}
